import { CommonModule } from '@angular/common';
import { Component, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ShareUser } from '../../../models/api/reports/report-helpers/search-users-response';
import { Snackbar } from '../../../services/snackbar/snackbar.service';
import { BaseComponent } from '../../base/base.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { CheckboxComponent } from '../../core/controls/checkbox.component';
import { InputComponent } from '../../core/controls/input.component';
import { PickListComponent } from '../../core/controls/pick-list.component';
import { ActionRowComponent } from '../../core/panels/action-row.component';
import { DialogComponent } from '../../core/panels/dialog.component';

interface PickListUser {
  label: string;
  email: string;
  value: string;
}

type ShareMode = 'report' | 'filter';

@Component({
  selector: 'app-share-dialog',
  imports: [
    CommonModule,
    PickListComponent,
    ActionRowComponent,
    ButtonComponent,
    ReactiveFormsModule,
    InputComponent,
    CheckboxComponent,
    DialogComponent,
  ],
  template: `
    <app-dialog
      #dialog
      [label]="dialogLabel"
      [focusOnShow]="false"
      width="55rem"
    >
      <ng-template #content>
        <div *ngTemplateOutlet="body"></div>
      </ng-template>
    </app-dialog>
    <ng-template #body>
      <form
        class="tw-flex tw-gap-2 tw-pt-2 tw-pb-8 tw-items-start"
        [formGroup]="fg"
      >
        <div class="tw-grow">
          <app-input
            [control]="fg.controls.find"
            label="Search users"
            [showHelpText]="false"
            placeholder="Last name, first name or full name"
            [showClear]="true"
            (keydown.enter)="find(); $event.preventDefault()"
          />
          <div
            class="tw-text-[80%] tw-px-4 tw-pt-1 tw-pb-2 tw-leading-[14px] tw-italic tw-text-gray-600"
          >
            Search by first name, last name, or full name. For example, enter
            John, John Smith, or Smith, John.
          </div>
        </div>
        <div class="tw-shrink-0">
          <app-button
            label="Search"
            emphasis="Low"
            [disabled]="!fg.controls.find.value || searching"
            [loading]="searching"
            (click)="find()"
          />
        </div>
        <div class="tw-shrink-0 tw-pl-8">
          <app-checkbox
            [control]="fg.controls.sedgwickUsers"
            label="Include Sedgwick users"
            [showHelpText]="false"
          ></app-checkbox>
        </div>
      </form>
      @if (searchPerformed && !authorized) {
        <div
          class="tw-bg-red-100 tw-border tw-border-red-400 tw-text-red-700 tw-px-4 tw-py-3 tw-rounded tw-mb-4"
        >
          You are not authorized to share
          {{ mode === 'report' ? 'reports' : 'filters' }} for this contract.
        </div>
      }
      <app-pick-list
        [showFilter]="false"
        [showMove]="false"
        [sourceControl]="availableControl"
        [targetControl]="selectedControl"
        labelName="label"
        keyName="value"
        sourceLabel="Search Results"
        targetLabel="Selected Users"
      />
      <app-action-row classes="tw-pt-4">
        <app-button
          right1
          [label]="shareButtonLabel"
          [disabled]="selectedControl.value.length === 0 || sharing"
          [loading]="sharing"
          (click)="share()"
        />
        <app-button right2 label="Cancel" emphasis="Low" (click)="close()" />
      </app-action-row>
    </ng-template>
  `,
  styles: ``,
})
export class ShareDialogComponent extends BaseComponent {
  private snackbar = inject(Snackbar);

  @ViewChild('dialog')
  dialog: DialogComponent;

  fg = new FormGroup({
    find: new FormControl<string>(null),
    sedgwickUsers: new FormControl<boolean>(true),
  });

  selectedControl = new FormControl<PickListUser[]>([]);
  availableControl = new FormControl<PickListUser[]>([]);

  mode: ShareMode = 'report';
  id: string;
  contractNumber: number;
  name: string;
  reportMasterId: string;
  searching = false;
  sharing = false;
  authorized = true;
  searchPerformed = false;

  get dialogLabel(): string {
    const prefix = this.mode === 'report' ? 'Share report' : 'Share filter';
    return this.name ? `${prefix} - ${this.name}` : prefix;
  }

  get shareButtonLabel(): string {
    return this.mode === 'report' ? 'Share report' : 'Share filter';
  }

  async find() {
    if (!this.fg.controls.find.value || this.searching) {
      return;
    }

    this.searching = true;
    this.searchPerformed = true;
    const searchTerm = this.fg.controls.find.value;

    try {
      const response = await this.api.reportHelpers.searchUsers(
        this.contractNumber,
        searchTerm,
        this.fg.controls.sedgwickUsers.value,
        this.reportMasterId,
      );

      this.authorized = response.authorized;

      if (response.authorized) {
        const users: PickListUser[] = response.users.map((user: ShareUser) => ({
          label: user.description,
          email: user.emailAddress,
          value: user.value,
        }));
        this.availableControl.setValue(users);

        if (users.length === 0) {
          this.snackbar.show(
            'No users found matching your search',
            'Info',
            'fa-light fa-info-circle',
            3000,
          );
        }
      } else {
        this.availableControl.setValue([]);
      }
    } finally {
      this.searching = false;
    }
  }

  show(
    mode: ShareMode,
    id: string,
    contractNumber: number,
    name: string,
    reportMasterId?: string,
  ) {
    this.mode = mode;
    this.id = id;
    this.contractNumber = contractNumber;
    this.name = name;
    this.reportMasterId = reportMasterId;
    this.reset();
    this.dialog.visible = true;
  }

  close() {
    this.dialog.visible = false;
  }

  private reset() {
    this.fg.reset({ find: null, sedgwickUsers: true });
    this.availableControl.setValue([]);
    this.selectedControl.setValue([]);
    this.authorized = true;
    this.searchPerformed = false;
  }

  async share() {
    if (this.selectedControl.value.length === 0 || this.sharing) {
      return;
    }

    this.sharing = true;
    const selectedUsers = this.selectedControl.value;
    let successCount = 0;
    let failCount = 0;

    try {
      for (const user of selectedUsers) {
        try {
          if (this.mode === 'report') {
            await this.api.reports.sharePersonalized(
              this.id,
              user.value,
              user.email,
            );
          } else {
            await this.api.filters.shareFilter(
              this.id,
              user.value,
              user.email,
            );
          }

          successCount++;
        } catch {
          failCount++;
        }
      }

      const itemType = this.mode === 'report' ? 'Report' : 'Filter';
      if (failCount === 0) {
        const message =
          selectedUsers.length > 1
            ? `${itemType} shared with ${selectedUsers.length} recipients`
            : `${itemType} shared with ${selectedUsers[0].email}`;
        this.snackbar.show(message, 'Success', 'fa-light fa-share', 3000);
        this.close();
      } else if (successCount > 0) {
        this.snackbar.show(
          `Shared with ${successCount} account(s), ${failCount} failed`,
          'Info',
          'fa-light fa-exclamation-triangle',
          5000,
        );
      } else {
        this.snackbar.show(
          `Failed to share ${this.mode}`,
          'Error',
          'fa-light fa-exclamation-triangle',
          5000,
        );
      }
    } finally {
      this.sharing = false;
    }
  }
}
